import type { ParsedStatement } from './types'

interface SourceLine {
    lineNumber: number
    text: string
}

export interface Block {
    startLine: number
    lines: SourceLine[]
}

/**
 * Split EN source into blocks separated by blank lines.
 * Each block may contain comment lines and statement lines.
 * Only blocks with non-comment content are returned.
 */
export function splitIntoBlocks(source: string): Block[] {
    const allLines = source.split('\n').map((text, idx) => ({ lineNumber: idx, text }))
    const blocks: Block[] = []
    let currentBlock: SourceLine[] = []

    for (const line of allLines) {
        if (line.text.trim() === '') {
            if (currentBlock.length > 0) {
                const hasStatement = currentBlock.some(
                    l => !l.text.trimStart().startsWith('#') && l.text.trim() !== ''
                )
                if (hasStatement) {
                    blocks.push({ startLine: currentBlock[0].lineNumber, lines: [...currentBlock] })
                }
                currentBlock = []
            }
        } else {
            currentBlock.push(line)
        }
    }

    if (currentBlock.length > 0) {
        const hasStatement = currentBlock.some(
            l => !l.text.trimStart().startsWith('#') && l.text.trim() !== ''
        )
        if (hasStatement) {
            blocks.push({ startLine: currentBlock[0].lineNumber, lines: [...currentBlock] })
        }
    }

    return blocks
}

const KEYWORD_RE = /^(do|needs|yields|at)\s*:/i

/**
 * Lightweight regex EN parser for UI card display.
 * Parses source string into ParsedStatement[] by splitting into blocks
 * and extracting subjects, do, needs, yields, at fields.
 */
export function parseENSource(source: string): ParsedStatement[] {
    const blocks = splitIntoBlocks(source)
    const statements: ParsedStatement[] = []

    for (const block of blocks) {
        const nonComment = block.lines.filter(
            l => !l.text.trimStart().startsWith('#') && l.text.trim() !== ''
        )
        if (nonComment.length === 0) continue

        let subjects: string[] = []
        let action = ''
        let needs: string[] = []
        let yields: string[] = []
        let location: string | null = null
        let startLine = nonComment[0].lineNumber

        for (const line of nonComment) {
            const trimmed = line.text.trim()
            const kwMatch = trimmed.match(KEYWORD_RE)
            if (kwMatch) {
                const keyword = kwMatch[1].toLowerCase()
                const value = trimmed.slice(kwMatch[0].length).trim()
                switch (keyword) {
                    case 'do':
                        action = value
                        break
                    case 'needs':
                        needs = value.split(',').map(s => s.trim()).filter(Boolean)
                        break
                    case 'yields':
                        yields = value.split(',').map(s => s.trim()).filter(Boolean)
                        break
                    case 'at':
                        location = value || null
                        break
                }
            } else {
                // First non-keyword line = subjects
                if (subjects.length === 0) {
                    subjects = trimmed.split(',').map(s => s.trim()).filter(Boolean)
                    startLine = line.lineNumber
                }
            }
        }

        if (subjects.length > 0 || action) {
            statements.push({ subjects, action, needs, yields, location, startLine })
        }
    }

    return statements
}
